import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Route, Switch, useRouteMatch, Redirect, useLocation, useHistory} from 'react-router-dom'

import {
    Box,
    Button,
    Divider,
    Grid,
    Heading,
    Stack,
    Text,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ChevronRightIcon} from '@salesforce/retail-react-app/app/components/icons'
import AccountDetail from '../account/profile'
import AddressBook from './addressBook'
import AccountOrders from './orders'
import ChangePassword from './changePassword'
import AccountWishlist from './wishlist/index'
import {messages, getNavLinks, INTL_BLOCKED_ACCOUNT_PATHS} from './constant'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import {useAuthHelper, AuthHelpers, useCustomer} from '@salesforce/commerce-sdk-react'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useAccountEdit, AccountEditProvider} from '../../contexts/account-edit-context'
import {useScript, setInitialScriptState} from '../../contexts/script-context'

import LoyaltyPoints from '../../components/loyalty-points'
import BarcodeComponent from '../../components/barcode-component'
import LeaveModal from '../../components/leave-modal'
import MaintenancePage from '../../components/maintenance-page'
import PaymentDetails from './payment-details'
import CommunicationPreferences from './communication-preferences'
import Seo from '../../components/seo'
import RouteGuard from '../../components/route-guard'
import {AccountBreadcrumb} from './partials/account-breadcrumb'
import {useBrand} from '../../contexts/brand-context'
import {getBrandDisplayName} from '../../utils/brandUtils'

// MRT multi site
import {getSiteEnvVarValue} from '../../utils/site-config'
import useHideLoyaltyUIForInternational from '@app/hooks/use-hide-loyalty-ui-for-international'

const onClient = typeof window !== 'undefined'

const Sidebar = ({loginPath, firstName, shouldHideLoyaltyUI, loyaltyID, accountNavLinks, isMainAccountPage, pathname, buildUrl, formatMessage, onSignoutClick}) => (
    <RouteGuard redirectTo={loginPath}>
        <Stack id="account-left-container" spacing={6} display={{base: 'none', lg: 'flex'}}>
            <Box w="100%">
                {firstName ? (
                    <Text textStyle="webH3" mb={2}>
                        <FormattedMessage defaultMessage="Welcome" id="account_dashboard.welcome" />
                        , {firstName}
                    </Text>
                ) : (
                    <Text textStyle="webH3" mb={2}>
                        <FormattedMessage defaultMessage="Welcome" id="account_dashboard.welcome" />
                    </Text>
                )}

                {!shouldHideLoyaltyUI && (
                    <>
                        <LoyaltyPoints isAccountDrawer={false} loyaltyId={loyaltyID} />

                        <Link to="/my-rewards" display={'inline-block'}>
                            <Text mt={4} variant="accountInfoSmall" textDecoration="underline">
                                <FormattedMessage
                                    defaultMessage="See My Rewards"
                                    id="account.rewards"
                                />
                            </Text>
                        </Link>
                    </>
                )}
            </Box>
            <Divider w="100%" borderColor="gray.500" />
            <Stack spacing={4} width="100%" maxW="225px">
                {accountNavLinks.map((link) => {
                    const accountPath = `/account${link.path}`
                    const isPersonal = link.path === '/personal'
                    const isActive =
                        (isMainAccountPage && isPersonal) ||
                        pathname === buildUrl(accountPath)

                    return (
                        <Button
                            key={link.name}
                            as={Link}
                            to={accountPath}
                            useNavLink
                            variant="accountDashboardlink"
                            isActive={isActive}
                        >
                            {formatMessage(messages[link.name])}
                        </Button>
                    )
                })}

                <Button onClick={onSignoutClick} variant="accountDashboardlink" mt={4}>
                    {formatMessage({
                        defaultMessage: 'Log Out',
                        id: 'account.logout_button.button.log_out'
                    })}
                </Button>
            </Stack>
        </Stack>
    </RouteGuard>
)

const MobileDashboard = ({firstName, shouldHideLoyaltyUI, loyaltyID, accountNavLinks, formatMessage, onSignoutClick}) => (
    <Box display={{base: 'block', lg: 'none'}} padding="24px 16px">
        <Stack spacing={6} align="center" mx="auto">
            <Box w="100%">
                <Heading as="h2" fontSize="sm" mb={6} textTransform="uppercase">
                    <FormattedMessage defaultMessage="Welcome" id="account_dashboard.welcome" />
                    , {firstName}
                </Heading>

                {!shouldHideLoyaltyUI && (
                    <>
                        {loyaltyID && <BarcodeComponent value={loyaltyID} />}
                        <LoyaltyPoints isAccountDrawer={false} />

                        <Link to="/my-rewards" display={'inline-block'}>
                            <Text mt={4} variant="accountInfoSmall" textDecoration="underline">
                                <FormattedMessage
                                    defaultMessage="See My Rewards"
                                    id="account.rewards"
                                />
                            </Text>
                        </Link>
                    </>
                )}
            </Box>
            <Divider w="100%" borderColor="grey.500" />
            <Stack width="100%" spacing={4}>
                {accountNavLinks.map((link) => (
                    <Button
                        key={link.name}
                        as={Link}
                        to={`/account${link.path}`}
                        useNavLink
                        variant="accountDashboardlink"
                        rightIcon={<ChevronRightIcon />}
                    >
                        {formatMessage(messages[link.name])}
                    </Button>
                ))}
                <Button
                    onClick={onSignoutClick}
                    variant="accountDashboardlink"
                    mt={4}
                    rightIcon={<ChevronRightIcon />}
                >
                    <Text as="span" variant="accountLink">
                        {formatMessage({
                            defaultMessage: 'Log Out',
                            id: 'account.logout_button.button.log_out'
                        })}
                    </Text>
                </Button>
            </Stack>
        </Stack>
    </Box>
)

const AccountContent = () => {
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {enableScript, disableScript} = useScript()
    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id

    // Called synchronously during render (before requestIdleCallback fires) so that
    // ScriptProvider's initial scripts object reflects the correct site-scoped value.
    if (getSiteEnvVarValue(config.app, 'IS_NOIBU_DISABLED_ACCOUNT', siteId) === 'true') {
        setInitialScriptState('noibu', false)
    }
    const {path, url} = useRouteMatch()
    const {formatMessage} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const {isRegistered, customerType, customerId} = customer
    const customerObj = useCustomer(
        {parameters: {customerId}},
        {enabled: isRegistered && !!customerId}
    )

    // Prevent hydration mismatch - customer data is only available on client after API call
    const [hasMounted, setHasMounted] = useState(false)
    useEffect(() => {
        setHasMounted(true)
    }, [])

    const logout = useAuthHelper(AuthHelpers.Logout)
    const location = useLocation()
    const history = useHistory()
    const navigate = useNavigation()
    const pathname = location.pathname

    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const {buildUrl, locale} = useMultiSite()
    const isInternational = Boolean(locale?.isSupportedByESW)
    const accountNavLinks = getNavLinks({isInternational})
    const loginPath = buildUrl('/login')
    const {brandId} = useBrand()

    const brandName = brandId.charAt(0).toUpperCase() + brandId.slice(1)
    const brandDisplayName = getBrandDisplayName(brandId, site?.alias)
    const shouldHideLoyaltyUI = useHideLoyaltyUIForInternational()

    const seoByRoute = {
        personal: {
            title: formatMessage(
                {
                    id: 'personal_info.page.title',
                    defaultMessage: 'My Account Details - {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            description: formatMessage(
                {
                    id: 'personal_info.page.description',
                    defaultMessage: 'My Account Details - {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            noIndex: true,
            noFollow: true
        },
        addresses: {
            title: formatMessage(
                {
                    id: 'address_book.page.title',
                    defaultMessage: 'My Account Details - {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            description: formatMessage(
                {
                    id: 'address_book.page.description',
                    defaultMessage: 'My Account Details - {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            noIndex: true,
            noFollow: true
        },
        payment: {
            title: formatMessage(
                {
                    id: 'payment_details.page.title',
                    defaultMessage: 'My Account Details - {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            description: formatMessage(
                {
                    id: 'payment_details.page.description',
                    defaultMessage: 'My Account Details - {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            noIndex: true,
            noFollow: true
        },
        wishlist: {
            title: formatMessage(
                {
                    id: 'my.wishlist.page.title',
                    defaultMessage: 'My Wishlist | {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            description: formatMessage(
                {
                    id: 'my.wishlist.page.description',
                    defaultMessage: 'Use the {brandId} wishlist to save your favourite styles.'
                },
                {brandId: brandName}
            ),
            noIndex: true,
            noFollow: true
        },
        orders: {
            title: formatMessage(
                {
                    id: 'account.order.history.page.title',
                    defaultMessage: 'My Order History | {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            description: formatMessage(
                {
                    id: 'order_history.page.description',
                    defaultMessage: 'Track your Order at {brandId}.'
                },
                {brandId: brandDisplayName}
            ),
            openGraphTags: [
                {
                    property: 'og:title',
                    content: formatMessage(
                        {
                            id: 'account.order.history.page.title',
                            defaultMessage: 'My Order History | {brandId}'
                        },
                        {brandId: brandDisplayName}
                    )
                },
                {
                    property: 'og:description',
                    content: formatMessage(
                        {
                            id: 'order_history.page.description',
                            defaultMessage: 'Track your Order at {brandId}.'
                        },
                        {brandId: brandDisplayName}
                    )
                }
            ],
            noIndex: true,
            noFollow: true
        },
        password: {
            title: formatMessage(
                {
                    id: 'change_password.page.title',
                    defaultMessage: 'My Account Details - {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            description: formatMessage(
                {
                    id: 'change_password.page.description',
                    defaultMessage: 'My Account Details - {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            noIndex: true,
            noFollow: true
        },
        email: {
            title: formatMessage(
                {
                    id: 'communication_preferences.page.title',
                    defaultMessage: 'My Account Details - {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            description: formatMessage(
                {
                    id: 'communication_preferences.page.description',
                    defaultMessage: 'My Account Details - {brandId}'
                },
                {brandId: brandDisplayName}
            ),
            noIndex: true,
            noFollow: true
        }
    }

    const renderGuarded = (seoProps, content) => (
        <>
            <Seo {...seoProps} />
            <RouteGuard redirectTo={loginPath}>{content}</RouteGuard>
        </>
    )

    const MAINTENANCE_ACCOUNT_WEB_ENABLED = getSiteEnvVarValue(config.app, 'MAINTENANCE_ACCOUNT_WEB_ENABLED', siteId)
    const MAINTENANCE_PAGE_IDS = getSiteEnvVarValue(config.app, 'MAINTENANCE_PAGE_IDS', siteId)

    const maintenancePageIds =
        (MAINTENANCE_ACCOUNT_WEB_ENABLED === true || MAINTENANCE_ACCOUNT_WEB_ENABLED === 'true') &&
        MAINTENANCE_PAGE_IDS
            ? MAINTENANCE_PAGE_IDS
            : ''
    const allKeys = ['personal', 'addresses', 'payment', 'wishlist', 'orders', 'password', 'email']
    const enabledKeys = maintenancePageIds.split(',').map((key) => key.trim())

    const maintenanceConfig = Object.fromEntries(
        allKeys.map((key) => [key, enabledKeys.includes(key)])
    )

    const maintenanceTexts = {
        personal: {
            heading: formatMessage({
                id: 'maintenance_page.personal.heading',
                defaultMessage: 'Personal Information'
            }),
            title: formatMessage({
                id: 'maintenance_page.personal.title',
                defaultMessage: 'Personal Information Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        addresses: {
            heading: formatMessage({
                id: 'account.address_book.address_heading',
                defaultMessage: 'Address Book'
            }),
            title: formatMessage({
                id: 'maintenance_page.addresses.title',
                defaultMessage: 'Address Book Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        payment: {
            heading: formatMessage({
                id: 'maintenance_page.payment.heading',
                defaultMessage: 'Payment Options'
            }),
            title: formatMessage({
                id: 'maintenance_page.payment.title',
                defaultMessage: 'Payment Options Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        wishlist: {
            heading: formatMessage({
                id: 'maintenance_page.wishlist.heading',
                defaultMessage: 'Wishlist'
            }),
            title: formatMessage({
                id: 'maintenance_page.wishlist.title',
                defaultMessage: 'Wishlist Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        orders: {
            heading: formatMessage({
                id: 'maintenance_page.orders.heading',
                defaultMessage: 'Order History'
            }),
            title: formatMessage({
                id: 'maintenance_page.orders.title',
                defaultMessage: 'Order History Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        password: {
            heading: formatMessage({
                id: 'maintenance_page.password.heading',
                defaultMessage: 'Change Password'
            }),
            title: formatMessage({
                id: 'maintenance_page.password.title',
                defaultMessage: 'Change Password Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        email: {
            heading: formatMessage({
                id: 'maintenance_page.email.heading',
                defaultMessage: 'Communication Preferences'
            }),
            title: formatMessage({
                id: 'maintenance_page.email.title',
                defaultMessage: 'Communication Preferences Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        }
    }

    useEffect(() => {
        if (getSiteEnvVarValue(config.app, 'IS_NOIBU_DISABLED_ACCOUNT', siteId) === 'true') {
            disableScript('noibu')
        } else {
            enableScript('noibu')
        }

        return () => {
            if (getSiteEnvVarValue(config.app, 'IS_NOIBU_DISABLED_ACCOUNT', siteId)) {
                enableScript('noibu')
            }
        }
    }, [])

    const onSignoutClick = async () => {
        const token = await getTokenWhenReady()
        await fetch(`${appOrigin}/process-logout?siteId=${siteId}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({token})
        })
        await logout.mutateAsync()
        navigate('/')
    }

    const {isEditing, isDirty, stopEditing} = useAccountEdit()
    const [showLeaveModal, setShowLeaveModal] = useState(false)
    const pendingLocation = useRef(null)

    useEffect(() => {
        const unblock = history.block((tx) => {
            if (isEditing && isDirty) {
                setShowLeaveModal(true)
                pendingLocation.current = tx
                return false
            }
            return true
        })

        return () => unblock()
    }, [isEditing, isDirty, history])

    const handleDiscardChanges = () => {
        setShowLeaveModal(false)
        if (pendingLocation.current) {
            // Stop editing before navigation to prevent the block from triggering again
            stopEditing()
            // Use setTimeout to ensure state updates are processed before navigation
            setTimeout(() => {
                history.push(pendingLocation.current.pathname)
                pendingLocation.current = null
            }, 0)
        } else {
            stopEditing()
        }
    }

    const handleCloseModal = () => setShowLeaveModal(false)

    if (customerType !== null && !isRegistered && onClient) {
        return <Redirect to={{pathname: loginPath, state: {directedFrom: '/account'}}} />
    }

    if (
        isInternational &&
        INTL_BLOCKED_ACCOUNT_PATHS.some((suffix) => pathname === buildUrl(`/account${suffix}`))
    ) {
        return <Redirect to={buildUrl('/account')} />
    }

    // Only access customer data after mount to prevent hydration mismatch
    const firstName = hasMounted ? customerObj.data?.firstName : undefined
    const loyaltyID = hasMounted
        ? customerObj.data?.c_b2cloyalty_loyaltyProgramMemberNumber
        : undefined

    const SMS_OPTIN_LOYALTY_ENABLED = getSiteEnvVarValue(config.app, 'SMS_OPTIN_LOYALTY_ENABLED', siteId)
    const SFSC_EMAIL_SUBSCRIPTION_ENABLED = getSiteEnvVarValue(config.app, 'SFSC_EMAIL_SUBSCRIPTION_ENABLED', siteId)

    const renderContent = () => (
        <Switch>
            <Route exact path={path}>
                {renderGuarded(
                    seoByRoute.personal,
                    maintenanceConfig.personal ? (
                        <MaintenancePage {...maintenanceTexts.personal} />
                    ) : (
                        <AccountDetail />
                    )
                )}
            </Route>
            <Route exact path={`${path}/personal`}>
                {renderGuarded(
                    seoByRoute.personal,
                    maintenanceConfig.personal ? (
                        <MaintenancePage {...maintenanceTexts.personal} />
                    ) : (
                        <AccountDetail />
                    )
                )}
            </Route>
            <Route exact path={`${path}/wishlist`}>
                <Redirect to={buildUrl('/wishlist')} />
            </Route>
            <Route exact path={`${path}/addresses`}>
                {renderGuarded(
                    seoByRoute.addresses,
                    maintenanceConfig.addresses ? (
                        <MaintenancePage {...maintenanceTexts.addresses} />
                    ) : (
                        <AddressBook />
                    )
                )}
            </Route>
            <Route exact path={`${path}/payment`}>
                {renderGuarded(
                    seoByRoute.payment,
                    maintenanceConfig.payment ? (
                        <MaintenancePage {...maintenanceTexts.payment} />
                    ) : (
                        <PaymentDetails />
                    )
                )}
            </Route>
            <Route path={`${path}/orders`}>
                {renderGuarded(
                    seoByRoute.orders,
                    maintenanceConfig.orders ? (
                        <MaintenancePage {...maintenanceTexts.orders} />
                    ) : (
                        <AccountOrders />
                    )
                )}
            </Route>
            <Route path={`${path}/password`}>
                {renderGuarded(
                    seoByRoute.password,
                    maintenanceConfig.password ? (
                        <MaintenancePage {...maintenanceTexts.password} />
                    ) : (
                        <ChangePassword customerEmail={customerObj.data?.email} />
                    )
                )}
            </Route>
            {(SMS_OPTIN_LOYALTY_ENABLED == 'true' || SFSC_EMAIL_SUBSCRIPTION_ENABLED == 'true') && (
                <Route path={`${path}/email`}>
                    {renderGuarded(
                        seoByRoute.email,
                        maintenanceConfig.email ? (
                            <MaintenancePage {...maintenanceTexts.email} />
                        ) : (
                            <CommunicationPreferences />
                        )
                    )}
                </Route>
            )}
        </Switch>
    )

    const accountBasePath = url.replace(/\/$/, '')
    const isAccountSubPage =
        pathname !== accountBasePath && pathname.startsWith(`${accountBasePath}/`)
    const isMainAccountPage = pathname === accountBasePath


    const isOrderDetailsPage =
        pathname.startsWith('/account/orders/') && pathname !== '/account/orders'

    return (
        <Box data-testid={isRegistered ? 'account-page' : 'account-page-skeleton'}>
            {isMobile && !isOrderDetailsPage ? (
                <AccountBreadcrumb pathname={location.pathname} />
            ) : null}
            {/* Mobile Dashboard - only on main /account page */}
            {isMainAccountPage && <MobileDashboard firstName={firstName} shouldHideLoyaltyUI={shouldHideLoyaltyUI} loyaltyID={loyaltyID} accountNavLinks={accountNavLinks} formatMessage={formatMessage} onSignoutClick={onSignoutClick} />}

            {/* Sub-pages: Single content render with responsive layout */}
            {isAccountSubPage && (
                <Grid
                    templateColumns={{
                        base: '1fr',
                        lg: 'minmax(0, min(340px, 40%)) minmax(60%, 1fr)'
                    }}
                    gap={{base: 0, lg: 125}}
                    alignItems="flex-start"
                    maxW={{base: 'full', lg: 'container.xxxl'}}
                    mx="auto"
                    padding={{base: '32px 16px 24px 16px', lg: '32px 24px 97px 24px'}}
                >
                    <Sidebar loginPath={loginPath} firstName={firstName} shouldHideLoyaltyUI={shouldHideLoyaltyUI} loyaltyID={loyaltyID} accountNavLinks={accountNavLinks} isMainAccountPage={isMainAccountPage} pathname={pathname} buildUrl={buildUrl} formatMessage={formatMessage} onSignoutClick={onSignoutClick} />
                    <Box
                        minW={0}
                        flex={
                            pathname === buildUrl('/account/payment') ||
                            pathname === buildUrl('/account/addresses')
                                ? '1'
                                : ''
                        }
                    >
                        {renderContent()}
                    </Box>
                </Grid>
            )}

            {/* Main account page desktop layout - sidebar + profile content */}
            {isMainAccountPage && (
                <Grid
                    display={{base: 'none', lg: 'grid'}}
                    templateColumns={{
                        lg: 'minmax(0, min(340px, 40%)) minmax(60%, 1fr)'
                    }}
                    gap={125}
                    alignItems="flex-start"
                    maxW="container.xxxl"
                    mx="auto"
                    padding="48px 24px 97px 24px"
                >
                    <Sidebar loginPath={loginPath} firstName={firstName} shouldHideLoyaltyUI={shouldHideLoyaltyUI} loyaltyID={loyaltyID} accountNavLinks={accountNavLinks} isMainAccountPage={isMainAccountPage} pathname={pathname} buildUrl={buildUrl} formatMessage={formatMessage} onSignoutClick={onSignoutClick} />
                    <Box minW={0}>{renderContent()}</Box>
                </Grid>
            )}

            <LeaveModal
                isOpen={showLeaveModal}
                onClose={handleCloseModal}
                onConfirm={handleDiscardChanges}
            />
        </Box>
    )
}

const Account = () => {
    return (
        <AccountEditProvider>
            <AccountContent />
        </AccountEditProvider>
    )
}

Account.getTemplateName = () => 'account'
Account.propTypes = {match: PropTypes.object}

export default Account
